import type { BillingStatus, BillingStatusDetail } from '@/api/billing'
import { daysLeft } from '@/lib/trial'

export type PlanPillTone = 'primary' | 'warning'

/** Tone + label for a billing status, shared so the sidebar badge and the Billing settings
 *  page's status header render the same visual language. `null` (rather than a "not
 *  entitled" tone) for `expired` — the non-dismissible trial-ended banner already makes that state
 *  impossible to miss, so a badge for it would be redundant everywhere it could appear. */
export function planStatusPill(
  status: BillingStatus,
  trialEndsAt: string | null
): { tone: PlanPillTone; label: string } | null {
  switch (status) {
    case 'trialing': {
      if (!trialEndsAt) return null
      const n = daysLeft(trialEndsAt)
      return { tone: 'warning', label: `Trial · ${n}d left` }
    }
    case 'active':
      return { tone: 'primary', label: 'Subscribed' }
    case 'past_due':
      return { tone: 'warning', label: 'Payment overdue' }
    case 'expired':
    case 'sample':
      return null
  }
}

export function formatBillingDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—'
}

export const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment overdue',
  expired: 'Trial ended',
}

/** "Annual plan · renews 12 Mar 2027" / "Free trial · ends …" — null when there's nothing to say. */
export function planSummary(data: BillingStatusDetail): string | null {
  const sub = data.subscription
  if (data.status === 'trialing') return `Free trial · ends ${formatBillingDate(data.trialEndsAt)}`
  if (sub && (data.status === 'active' || data.status === 'past_due')) {
    const plan = `${sub.planCode.charAt(0)}${sub.planCode.slice(1).toLowerCase()} plan`
    if (sub.cancelAtCycleEnd) return `${plan} · ends ${formatBillingDate(sub.currentEnd)}`
    if (sub.currentEnd) return `${plan} · renews ${formatBillingDate(sub.currentEnd)}`
    return plan
  }
  return STATUS_LABEL[data.status] ?? null
}
